/**
 * Nutrition-program persistence schema.
 *
 * Invariants:
 * - Program date ranges may not overlap for the same user.
 * - Day overrides depend on macro-distribution presence when required.
 *
 * @see `packages/core/docs/database.md`
 */
import { doublePrecision, foreignKey, jsonb, pgTable, unique, uuid } from "drizzle-orm/pg-core";
import { dependsOn, id, temporalDate, timestamps } from "../../utils/sql";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import type { Rmr } from "./rmr";
import type { MacroDistribution } from "./macro-distribution";
import type { Calendar } from "@corpuscomplete/core/common/calendar";
import { relations } from "drizzle-orm";
import { user } from "../../auth/core/user.sql";
import { bodyMetricGoal } from "../../body/body-metric-goal/body-metric-goal.sql";

export const nutritionProgram = pgTable(
  "nutrition_program",
  {
    ...id,
    ...timestamps,
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    dateStart: temporalDate("date_start").notNull(),
    dateEnd: temporalDate("date_end"),
  },
  (t) => [
    unique("nutrition_program_id_user_unique").on(t.id, t.userId),
    // This doesn't do anything in current drizzle version, we manually add this in a custom migration
    // Move it here if possible in the future
    // sql`EXCLUDE USING GIST (${t.userId} WITH =, tsrange(${t.dateStart}, ${t.dateEnd}, '[]') WITH &&)`.as(
    //   "nutrition_program_overlap",
    // ),
  ],
);

export const nutritionProgramRelations = relations(nutritionProgram, ({ one, many }) => ({
  expenditure: one(nutritionProgramExpenditure),
  days: many(nutritionProgramDay),
}));

export const nutritionProgramExpenditure = pgTable("nutrition_program_expenditure", {
  programId: uuid("program_id")
    .primaryKey()
    .references(() => nutritionProgram.id),
  rmrParameters: jsonb("rmr_parameters").$type<Rmr.Parameters>().notNull(),
  activityFactor: doublePrecision("activity_factor").notNull(),
  /**
   * User's personal metabolic adjustment in calories.
   * Accounts for individual metabolic variation not captured by RMR formulas.
   * Positive values indicate higher actual expenditure than calculated.
   */
  calibrationOffset: doublePrecision("calibration_offset").notNull(),
  goalId: uuid("goal_id").references(() => bodyMetricGoal.id),
  /**
   * Daily calorie deficit/surplus derived from target weight change rate.
   * Calculated as: (lbs_per_week × 3500) / 7.
   * Example: -500 for 1 lb/week loss, +500 for 1 lb/week gain.
   */
  goalOffset: doublePrecision("offset").notNull(),
});

export const nutritionProgramExpenditureRelations = relations(
  nutritionProgramExpenditure,
  ({ one }) => ({
    program: one(nutritionProgram, {
      fields: [nutritionProgramExpenditure.programId],
      references: [nutritionProgram.id],
    }),
    goal: one(bodyMetricGoal, {
      fields: [nutritionProgramExpenditure.goalId],
      references: [bodyMetricGoal.id],
    }),
  }),
);

export const nutritionProgramDay = pgTable(
  "nutrition_program_day",
  {
    ...id,
    // Will probably add this name field at some point
    // name: text("name").notNull(),
    programId: uuid("program_id")
      .references(() => nutritionProgram.id)
      .notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    dateSelector: jsonb("date_selector").$type<Calendar.DateSelector>(),

    macroDistribution: jsonb("macro_distribution").$type<MacroDistribution.Info>(),
    expenditureOverrideActivityFactor: doublePrecision("expenditure_override_activity_factor"),
    expenditureAdditionalOffset: doublePrecision("expenditure_additional_offset"),

    nutrientTargets: jsonb("nutrient_targets").$type<Nutrient.Targets>().notNull(),
  },
  (t) => [
    unique("nutrition_program_day_id_user_unique").on(t.id, t.userId),
    foreignKey({
      name: "nutrition_program_day_program_user_fk",
      columns: [t.programId, t.userId],
      foreignColumns: [nutritionProgram.id, nutritionProgram.userId],
    }),
    dependsOn("nutrition_program_day_activity_factor_depends_on", {
      depends: t.expenditureOverrideActivityFactor,
      on: t.macroDistribution,
    }),
    dependsOn("nutrition_program_day_offset_depends_on", {
      depends: t.expenditureAdditionalOffset,
      on: t.macroDistribution,
    }),
  ],
);

export const nutritionProgramDayRelations = relations(nutritionProgramDay, ({ one }) => ({
  program: one(nutritionProgram, {
    fields: [nutritionProgramDay.programId],
    references: [nutritionProgram.id],
  }),
}));
